import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { VideoPrompt, VideoProvider } from '../types';

export function useProviders() {
  return useQuery({
    queryKey: ['providers'],
    queryFn: () => api.getProviders(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useJobs(options?: { limit?: number; offset?: number; status?: string }) {
  return useQuery({
    queryKey: ['jobs', options],
    queryFn: () => api.getJobs(options),
    refetchInterval: 5000, // Poll every 5 seconds for updates
  });
}

export function useJob(jobId: string) {
  return useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.getJob(jobId),
    refetchInterval: (query) => {
      const job = query.state.data;
      // Stop polling when job is complete or failed
      if (job?.status === 'completed' || job?.status === 'failed') {
        return false;
      }
      return 2000; // Poll every 2 seconds while processing
    },
  });
}

export function useGenerateVideo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ prompt, provider }: { prompt: VideoPrompt; provider?: VideoProvider }) =>
      api.generateVideo(prompt, provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['quota'] });
    },
  });
}

export function useCancelJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => api.cancelJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

export function useEstimateCost() {
  return useMutation({
    mutationFn: ({ prompt, provider }: { prompt: VideoPrompt; provider?: VideoProvider }) =>
      api.estimateCost(prompt, provider),
  });
}

export function useUserStats() {
  return useQuery({
    queryKey: ['stats'],
    queryFn: () => api.getStats(),
    staleTime: 1000 * 60, // 1 minute
  });
}

export function useUserQuota() {
  return useQuery({
    queryKey: ['quota'],
    queryFn: () => api.getQuota(),
    staleTime: 1000 * 30, // 30 seconds
  });
}
